import type { AbstractPartDefinition } from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import * as THREE from "three";
import { Alignment } from "../enum/Alignment";
import { OrientationOnRect } from "../enum/OrientationOnRect";
import type { AddableGridInfos } from "../interfaces/AddableGridInfos";
import { RectPropertiesBySide } from "../structure/RectProperties";

function getCut(
  orientation: number,
  orientationOnRect: OrientationOnRect,
  cutX?: number,
  cutY?: number,
): number | undefined {
  const a = orientation % 180 !== 0;
  const b = orientationOnRect === OrientationOnRect.HORIZONTAL;

  if (a !== b) {
    return cutX ? cutX / 2 : undefined;
  }
  return cutY ? cutY / 2 : undefined;
}

/**
 * Same formula as AddableBuildStep.updateSettingsFromDrag (without clamping to ≥ 0).
 */
function marginFromOffset(
  offset: number,
  size: number,
  count: number,
  n: number,
  padding: number,
  alignmentCtx: Alignment,
): number {
  const pos = offset + size / 2;
  if (count > 1 || alignmentCtx !== Alignment.CENTER) {
    const denom = 1 - 2 * n;
    if (Math.abs(denom) < 0.001) {
      return Number.NaN;
    }
    return (pos - size * n - padding * denom) / denom;
  }
  return offset;
}

export interface MarginAxisRange {
  min: number;
  max: number;
}

export interface AddableMarginDragRanges {
  horizontal: MarginAxisRange;
  vertical: MarginAxisRange;
}

/**
 * Derive min/max horizontal and vertical margins from the same padded 3D bounds
 * used for Ctrl+drag on addables (surface drag constraint corners → margin pairs).
 */
export function computeAddableMarginRangesFromDragPart(
  part: AbstractPartDefinition<string>,
  option: Pick<AddableGridInfos, "orientation" | "partSelection">,
): AddableMarginDragRanges | null {
  const constraint = part.dragConstraint;
  const ctx = part.settingsContext;
  if (!constraint || constraint.type !== "surface" || !ctx) {
    return null;
  }

  const side = ctx.side as Sides;
  const rectProps = RectPropertiesBySide[side];
  if (!rectProps) {
    return null;
  }

  const { horizontalDir, verticalDir } = rectProps;

  const logicalRectBoundsMin = new THREE.Vector3(
    ...(ctx.logicalRectBoundsMin as [number, number, number]),
  );
  const logicalRectBoundsMax = new THREE.Vector3(
    ...(ctx.logicalRectBoundsMax as [number, number, number]),
  );
  const logicalRectCenter = new THREE.Vector3(
    ...(ctx.logicalRectCenter as [number, number, number]),
  );

  const horizontalCount = (ctx.horizontalCount as number) ?? 1;
  const verticalCount = (ctx.verticalCount as number) ?? 1;
  const horizontalN = (ctx.horizontalN as number) ?? 0.5;
  const verticalN = (ctx.verticalN as number) ?? 0.5;
  const horizontalAlignmentCtx =
    (ctx.horizontalAlignment as Alignment) ?? Alignment.CENTER;
  const verticalAlignmentCtx =
    (ctx.verticalAlignment as Alignment) ?? Alignment.CENTER;

  const rectSize = logicalRectBoundsMax.clone().sub(logicalRectBoundsMin);
  const horizontalSize = Math.abs(rectSize.dot(horizontalDir));
  const verticalSize = Math.abs(rectSize.dot(verticalDir));

  const { cutX, cutY } =
    (option.partSelection as { cutX?: number; cutY?: number }) ?? {};
  const paddingX =
    getCut(option.orientation, OrientationOnRect.HORIZONTAL, cutX, cutY) || 0;
  const paddingY =
    getCut(option.orientation, OrientationOnRect.VERTICAL, cutX, cutY) || 0;

  const { min: bMin, max: bMax } = constraint.bounds;
  const corners: THREE.Vector3[] = [];
  for (const x of [bMin[0], bMax[0]]) {
    for (const y of [bMin[1], bMax[1]]) {
      for (const z of [bMin[2], bMax[2]]) {
        corners.push(new THREE.Vector3(x, y, z));
      }
    }
  }

  const horizontalMargins: number[] = [];
  const verticalMargins: number[] = [];

  for (const p of corners) {
    const offsetFromCenter = p.clone().sub(logicalRectCenter);
    let horizontalOffset = offsetFromCenter.dot(horizontalDir);
    const orient = option.orientation ?? 0;
    if (
      rectProps.horizontal === "z" &&
      side === Sides.LEFT &&
      orient % 180 !== 0
    ) {
      horizontalOffset = -horizontalOffset;
    }
    const verticalOffset = offsetFromCenter.dot(verticalDir);

    const mH = marginFromOffset(
      horizontalOffset,
      horizontalSize,
      horizontalCount,
      horizontalN,
      paddingX,
      horizontalAlignmentCtx,
    );
    const mV = marginFromOffset(
      verticalOffset,
      verticalSize,
      verticalCount,
      verticalN,
      paddingY,
      verticalAlignmentCtx,
    );

    if (Number.isFinite(mH)) horizontalMargins.push(mH);
    if (Number.isFinite(mV)) verticalMargins.push(mV);
  }

  if (horizontalMargins.length === 0 || verticalMargins.length === 0) {
    return null;
  }

  const result: AddableMarginDragRanges = {
    horizontal: {
      min: Math.min(...horizontalMargins),
      max: Math.max(...horizontalMargins),
    },
    vertical: {
      min: Math.min(...verticalMargins),
      max: Math.max(...verticalMargins),
    },
  };
  return result;
}
